import { resetPasswordSchema } from "@rmnlng/shared";
import { useForm } from "@tanstack/react-form";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { FieldError } from "@/components/FieldError";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMe, useResetPassword } from "@/lib/auth";
import { useErrorMessage } from "@/lib/error-message";
import { useT } from "@/lib/i18n";

/**
 * Step 2 of the "I forgot my password" flow.
 *
 * Reads `?token=…` from the URL (planted there by the email link),
 * collects a new password + confirm, and POSTs to
 * `/auth/password/reset`. On success: navigate to `/login` with a
 * flash message in router state ("Password updated — sign in again").
 *
 * **Bad tokens render an early-error pane**, not a hidden 400. We
 * surface the typical failure mode (link expired or already used)
 * with a single CTA back to `/forgot-password`. Server returns 400
 * for *any* invalid token (we don't differentiate the cause for
 * anti-enumeration reasons), so the copy is the same regardless.
 */
export function ResetPasswordPage() {
  const { data: user } = useMe();
  const reset = useResetPassword();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const t = useT();
  const errorMessage = useErrorMessage();

  const token = params.get("token") ?? "";

  const form = useForm({
    // Pre-fill `token` from the URL — never asked of the user. The Zod
    // schema's regex (`^[a-f0-9]{64}$`) is the boundary check; an
    // empty/garbled token will fail at submit-time validation, but we
    // also short-circuit on missing-or-malformed below for a friendlier
    // error UX.
    defaultValues: { token, password: "", confirmPassword: "" },
    validators: { onChange: resetPasswordSchema },
    onSubmit: async ({ value }) => {
      await reset.mutateAsync(value);
      // Land on /login with a flash message. The login page reads
      // `location.state.flash` and renders a one-time success banner,
      // then clears it on next navigation.
      navigate("/login", {
        replace: true,
        state: { flash: t("reset.success_toast") },
      });
    },
  });

  // Same already-signed-in guard as Login/SignUp. Conceivable a user
  // following an old reset link is already signed in on a fresh
  // session; sending them home avoids an unnecessary password change.
  if (user) return <Navigate to="/" replace />;

  // Missing or malformed token: skip the form entirely and surface the
  // "this link doesn't work" pane. Saves the user from filling in
  // fields they can't submit.
  if (!/^[a-f0-9]{64}$/.test(token)) return <InvalidLinkPane />;

  return (
    <div className="flex min-h-dvh w-full items-stretch justify-center bg-bg">
      <div className="flex w-full max-w-[440px] flex-col px-7 pt-12 pb-8">
        <div className="mb-12">
          <Logo className="size-12" />
        </div>

        <h1 className="mb-2.5 text-[32px] font-bold leading-[1.1] tracking-[-0.03em]">{t("reset.title")}</h1>
        <p className="mb-9 text-[15px] leading-[1.5] text-text-3">{t("reset.subtitle")}</p>

        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          noValidate
        >
          <form.Field name="password">
            {(field) => (
              <div>
                <label htmlFor={field.name} className="mb-1.5 block text-[12px] font-medium text-text-2">
                  {t("reset.password")}
                </label>
                <Input
                  id={field.name}
                  type="password"
                  // `new-password` so password managers offer to generate +
                  // store rather than autofilling the (now-invalid) old one.
                  autoComplete="new-password"
                  placeholder={t("reset.password_placeholder")}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
                <FieldError errors={field.state.meta.errors} touched={field.state.meta.isTouched} />
              </div>
            )}
          </form.Field>

          <form.Field name="confirmPassword">
            {(field) => (
              <div>
                <label htmlFor={field.name} className="mb-1.5 block text-[12px] font-medium text-text-2">
                  {t("reset.confirm_password")}
                </label>
                <Input
                  id={field.name}
                  type="password"
                  autoComplete="new-password"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
                <FieldError errors={field.state.meta.errors} touched={field.state.meta.isTouched} />
              </div>
            )}
          </form.Field>

          {reset.error && (
            <p className="text-[13px] text-[color:var(--again)]" role="alert">
              {/* `useErrorMessage` resolves the localized copy from
                  the server's `code` field — no more substring
                  sniffing on the English message text. */}
              {errorMessage(reset.error)}
            </p>
          )}

          <form.Subscribe
            selector={(s) => ({
              canSubmit: s.canSubmit,
              isSubmitting: s.isSubmitting,
            })}
          >
            {({ canSubmit, isSubmitting }) => (
              <Button type="submit" size="block" className="mt-3" disabled={!canSubmit || isSubmitting}>
                {isSubmitting ? t("reset.submitting") : t("reset.submit")}
              </Button>
            )}
          </form.Subscribe>
        </form>

        <div className="mt-auto pt-6 text-center text-[13px] text-text-3">
          <Link to="/login" className="font-medium text-[color:var(--accent)]">
            {t("forgot.back_to_login")}
          </Link>
        </div>
      </div>
    </div>
  );
}

/**
 * Rendered when the URL token is missing or malformed (and as a
 * fallback for "the link doesn't work"). One-action pane: go request
 * a fresh link.
 */
function InvalidLinkPane() {
  const t = useT();
  return (
    <div className="flex min-h-dvh w-full items-stretch justify-center bg-bg">
      <div className="flex w-full max-w-[440px] flex-col px-7 pt-12 pb-8">
        <div className="mb-12">
          <Logo className="size-12" />
        </div>

        <h1 className="mb-2.5 text-[32px] font-bold leading-[1.1] tracking-[-0.03em]">
          {t("reset.invalid_link_title")}
        </h1>
        <p className="mb-9 text-[15px] leading-[1.5] text-text-3">{t("reset.invalid_link_body")}</p>

        <Link
          to="/forgot-password"
          className="inline-flex h-12 items-center justify-center rounded-[10px] bg-[color:var(--accent)] px-6 text-[15px] font-semibold text-white"
        >
          {t("reset.request_new_link")}
        </Link>

        <div className="mt-auto pt-6 text-center text-[13px] text-text-3">
          <Link to="/login" className="font-medium text-[color:var(--accent)]">
            {t("forgot.back_to_login")}
          </Link>
        </div>
      </div>
    </div>
  );
}
